import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import {
  Line, LineChart, BarChart, Bar,
  ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from "recharts";
import { TrendingUp, Trophy, AlertTriangle, Award, Activity, GraduationCap, Lock, Sparkles, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

/**
 * Personal Analytics — the "mirror" page.
 *
 * Tier gating (Feb 2026):
 *   * Free    → locked teaser page (Journal + Grader not available on Free)
 *   * Basic   → full page, but the Confluence Grader chart is
 *               overlay-locked with an upgrade CTA (parity with the way
 *               locked lessons render in the course view)
 *   * Premium → full access
 *
 * Renders 5 charts + a highlight strip pulled from a single
 * ``/api/analytics/me`` call. All UI strings routed through i18n so
 * Spanish + French members read their P&L in their own words. Trading
 * jargon (R, R-multiple, P&L, MA, H4) is intentionally kept in English
 * because that's how the audience reads it in the wild.
 */
export default function Analytics({ embedded = false }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [windowDays, setWindowDays] = useState(90);
  const [loading, setLoading] = useState(true);

  const tier = user?.tier || "free";
  const isFree = tier === "free" && user?.role !== "admin";
  const isBasic = tier === "basic";

  useEffect(() => {
    if (isFree) { setLoading(false); return; }
    setLoading(true);
    api.get(`/analytics/me?days=${windowDays}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [windowDays, isFree]);

  // ── Free-tier teaser ────────────────────────────────────────────
  if (isFree) return <FreeTeaser />;

  return (
    <div data-testid="analytics-page" className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className={embedded ? "sr-only" : ""}>
          <div className="text-xs uppercase tracking-[0.28em] text-blue-800 font-semibold mb-1">
            <Activity size={12} className="inline mr-1" /> {t("analyticsPage.eyebrow")}
          </div>
          <h1 className="font-display font-black text-3xl text-slate-900 tracking-tight">
            {t("analyticsPage.title")}
          </h1>
          <p className="mt-1 text-sm text-slate-500 max-w-xl">
            {t("analyticsPage.subtitle")}
          </p>
        </div>
        <div className="flex gap-1">
          {[30, 60, 90, 180, 365].map((d) => (
            <button
              key={d}
              onClick={() => setWindowDays(d)}
              data-testid={`analytics-window-${d}`}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${
                windowDays === d
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-slate-500 text-sm">{t("analyticsPage.loading")}</div>}

      {!loading && data && (
        <>
          {/* Summary strip */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label={t("analyticsPage.stats.trades")}   value={data.summary.total_trades} />
            <Stat label={t("analyticsPage.stats.closed")}   value={data.summary.closed_trades} />
            <Stat label={t("analyticsPage.stats.winRate")}  value={data.summary.win_rate != null ? `${data.summary.win_rate}%` : "—"} accent={data.summary.win_rate != null && data.summary.win_rate >= 50 ? "emerald" : "slate"} />
            <Stat label={t("analyticsPage.stats.totalPnl")} value={data.summary.total_pnl != null ? `$${data.summary.total_pnl.toFixed(0)}` : "—"} accent={data.summary.total_pnl > 0 ? "emerald" : data.summary.total_pnl < 0 ? "rose" : "slate"} />
            <Stat label={t("analyticsPage.stats.avgR")}     value={data.summary.avg_r_multiple != null ? `${data.summary.avg_r_multiple}R` : "—"} />
          </div>

          {(data.win_rate_weekly.length + data.r_histogram.reduce((s, x) => s + x.count, 0)) === 0 && <EmptyState />}

          {/* Win rate + P&L combined */}
          <ChartCard title={t("analyticsPage.charts.winRateTitle")} subtitle={t("analyticsPage.charts.winRateSub")} icon={TrendingUp}>
            {data.win_rate_weekly.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data.win_rate_weekly} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="week" fontSize={11} stroke="#64748b" />
                  <YAxis fontSize={11} stroke="#64748b" domain={[0, 100]} unit="%" />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <ReferenceLine y={50} stroke="#cbd5e1" strokeDasharray="4 4" />
                  <Line type="monotone" dataKey="win_rate" stroke="#1e40af" strokeWidth={2.5} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : <EmptyChart hint={t("analyticsPage.charts.winRateEmpty")} />}
          </ChartCard>

          {/* R-multiple histogram */}
          <ChartCard title={t("analyticsPage.charts.rHistTitle")} subtitle={t("analyticsPage.charts.rHistSub")} icon={Activity}>
            {data.r_histogram.some((x) => x.count > 0) ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.r_histogram} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="bucket" fontSize={10} stroke="#64748b" />
                  <YAxis fontSize={11} stroke="#64748b" allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {data.r_histogram.map((entry, i) => {
                      const negative = entry.bucket.startsWith("<") || entry.bucket.startsWith("-");
                      return <cell key={i} fill={negative ? "#ef4444" : "#10b981"} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyChart hint={t("analyticsPage.charts.rHistEmpty")} />}
          </ChartCard>

          {/* Grader trend + Discipline side-by-side on desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <PremiumLockable locked={isBasic} label={t("analyticsPage.premiumLock.label")}>
              <ChartCard title={t("analyticsPage.charts.graderTitle")} subtitle={t("analyticsPage.charts.graderSub")} icon={Award}>
                {data.grader_trend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={data.grader_trend} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="week" fontSize={11} stroke="#64748b" />
                      <YAxis fontSize={11} stroke="#64748b" domain={[0, 100]} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                      <ReferenceLine y={70} stroke="#cbd5e1" strokeDasharray="4 4" label={{ value: "B", fontSize: 10, fill: "#94a3b8" }} />
                      <Line type="monotone" dataKey="avg_score" stroke="#d97706" strokeWidth={2.5} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <EmptyChart hint={t("analyticsPage.charts.graderEmpty")} />}
              </ChartCard>
            </PremiumLockable>

            <ChartCard title={t("analyticsPage.charts.disciplineTitle")} subtitle={t("analyticsPage.charts.disciplineSub")} icon={TrendingUp}>
              {data.discipline_trend.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={data.discipline_trend} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="week" fontSize={11} stroke="#64748b" />
                    <YAxis fontSize={11} stroke="#64748b" domain={[0, 100]} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Line type="monotone" dataKey="score" stroke="#059669" strokeWidth={2.5} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <EmptyChart hint={t("analyticsPage.charts.disciplineEmpty")} />}
            </ChartCard>
          </div>

          {/* Cumulative lessons */}
          <ChartCard title={t("analyticsPage.charts.learningTitle")} subtitle={t("analyticsPage.charts.learningSub")} icon={GraduationCap}>
            {data.cumulative_lessons.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={data.cumulative_lessons} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" fontSize={10} stroke="#64748b" />
                  <YAxis fontSize={11} stroke="#64748b" allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Line type="monotone" dataKey="count" stroke="#0284c7" strokeWidth={2.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : <EmptyChart hint={t("analyticsPage.charts.learningEmpty")} />}
          </ChartCard>

          {/* Best / worst */}
          {(data.best_trade || data.worst_trade) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <HighlightCard trade={data.best_trade}  tone="emerald" icon={Trophy}         title={t("analyticsPage.highlights.best")} />
              <HighlightCard trade={data.worst_trade} tone="rose"    icon={AlertTriangle}  title={t("analyticsPage.highlights.worst")} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, accent = "slate" }) {
  const tones = { emerald: "text-emerald-700", rose: "text-rose-700", slate: "text-slate-900" };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={`mt-1 font-display font-black text-2xl ${tones[accent]}`}>{value}</div>
    </div>
  );
}

function ChartCard({ title, subtitle, icon: Icon, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-blue-800 font-semibold mb-1">
        <Icon size={12} /> {title}
      </div>
      <div className="text-xs text-slate-500 mb-4">{subtitle}</div>
      {children}
    </div>
  );
}

function EmptyChart({ hint }) {
  return (
    <div className="h-40 flex items-center justify-center text-slate-400 text-sm italic">
      {hint}
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border-2 border-dashed border-slate-200 p-10 text-center">
      <Activity size={28} className="mx-auto text-slate-400 mb-2" />
      <div className="font-display font-semibold text-slate-800">{t("analyticsPage.empty.title")}</div>
      <div className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
        {/* i18n embed pattern — <j> and <g> route the two inline links
            without hardcoding word order, so Spanish/French can put the
            noun before or after the verb naturally. */}
        <Trans
          i18nKey="analyticsPage.empty.body"
          components={{
            j: <Link to="/resources?tab=journal" className="text-blue-800 font-semibold underline" />,
            g: <Link to="/resources?tab=grader"  className="text-blue-800 font-semibold underline" />,
          }}
        />
      </div>
    </div>
  );
}

function HighlightCard({ trade, tone, icon: Icon, title }) {
  const { t } = useTranslation();
  if (!trade) return null;
  const tones = {
    emerald: { border: "border-emerald-300", bg: "bg-emerald-50/60", text: "text-emerald-800" },
    rose:    { border: "border-rose-300",    bg: "bg-rose-50/60",    text: "text-rose-800" },
  }[tone];
  return (
    <div className={`rounded-2xl border-2 ${tones.border} ${tones.bg} p-5`}>
      <div className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold ${tones.text}`}>
        <Icon size={12} /> {title}
      </div>
      <div className="mt-2 flex items-baseline gap-3">
        <div className="font-display font-black text-3xl text-slate-900">
          ${trade.pnl?.toFixed(2)}
        </div>
        {trade.r_multiple != null && (
          <div className={`text-sm font-semibold ${tones.text}`}>{trade.r_multiple}R</div>
        )}
      </div>
      <div className="mt-1 text-sm text-slate-700">
        <span className="font-semibold">{trade.instrument}</span>
        {trade.direction && <span> · {trade.direction}</span>}
        {trade.grader_grade && <span> · {t("analyticsPage.highlights.grade", { grade: trade.grader_grade })}</span>}
      </div>
      {trade.notes && (
        <div className="mt-2 text-xs text-slate-600 italic line-clamp-3">"{trade.notes}"</div>
      )}
    </div>
  );
}

/**
 * Wraps a chart card in a Premium-lock overlay when ``locked`` is true.
 */
function PremiumLockable({ locked, label, children }) {
  const { t } = useTranslation();
  if (!locked) return children;
  return (
    <div className="relative">
      <div className="pointer-events-none opacity-30 blur-[2px] select-none">{children}</div>
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="rounded-xl bg-slate-950/95 text-white px-4 py-3 max-w-xs shadow-lg text-center">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-amber-400 font-bold mb-1">
            <Lock size={11} /> {t("analyticsPage.premiumLock.pill")}
          </div>
          <div className="text-sm font-semibold leading-tight">
            {t("analyticsPage.premiumLock.message", { label })}
          </div>
          <Link
            to="/pricing"
            data-testid="analytics-grader-upgrade"
            className="mt-2 inline-flex items-center gap-1 text-amber-300 hover:text-amber-200 text-xs font-semibold"
          >
            {t("analyticsPage.premiumLock.cta")} <ChevronRight size={12} />
          </Link>
        </div>
      </div>
    </div>
  );
}

/** Locked-page teaser for Free-tier users. */
function FreeTeaser() {
  const { t } = useTranslation();
  return (
    <div data-testid="analytics-page-locked" className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.28em] text-blue-800 font-semibold mb-1">
          <Activity size={12} className="inline mr-1" /> {t("analyticsPage.eyebrow")}
        </div>
        <h1 className="font-display font-black text-3xl text-slate-900 tracking-tight">
          {t("analyticsPage.title")}
        </h1>
        <p className="mt-1 text-sm text-slate-500 max-w-xl">
          {t("analyticsPage.subtitleFreeTeaser")}
        </p>
      </div>

      <div className="relative overflow-hidden rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 via-white to-blue-50 p-8">
        <div className="max-w-xl">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] text-amber-700 font-bold mb-3">
            <Lock size={12} /> {t("analyticsPage.freeTeaser.lockedPill")}
          </div>
          <h2 className="font-display font-black text-2xl text-slate-900 leading-tight">
            {t("analyticsPage.freeTeaser.title")}
          </h2>
          <p className="mt-2 text-sm text-slate-700 leading-relaxed">
            <Trans
              i18nKey="analyticsPage.freeTeaser.body"
              components={{
                j: <span className="font-semibold" />,
                g: <span className="font-semibold" />,
              }}
            />
          </p>
          <Link to="/pricing" data-testid="analytics-upgrade-free">
            <Button className="mt-5 h-11 px-6 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
              <Sparkles size={14} className="mr-2" /> {t("analyticsPage.freeTeaser.cta")}
            </Button>
          </Link>
        </div>
      </div>

      {/* Sample-chart preview strip so free users see what they're missing */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 opacity-70">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-[10px] uppercase tracking-widest text-blue-800 font-semibold mb-1">
            <TrendingUp size={11} className="inline mr-1" /> {t("analyticsPage.freeTeaser.sampleWinRate")}
          </div>
          <div className="text-xs text-slate-500">{t("analyticsPage.freeTeaser.sampleWinRateSub")}</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-[10px] uppercase tracking-widest text-blue-800 font-semibold mb-1">
            <Activity size={11} className="inline mr-1" /> {t("analyticsPage.freeTeaser.sampleR")}
          </div>
          <div className="text-xs text-slate-500">{t("analyticsPage.freeTeaser.sampleRSub")}</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-[10px] uppercase tracking-widest text-blue-800 font-semibold mb-1">
            <Award size={11} className="inline mr-1" /> {t("analyticsPage.freeTeaser.sampleDiscipline")}
          </div>
          <div className="text-xs text-slate-500">{t("analyticsPage.freeTeaser.sampleDisciplineSub")}</div>
        </div>
      </div>
    </div>
  );
}
